import { useState, useEffect } from "react";
import { Box, Typography, Button, Card, useMediaQuery, useTheme } from "@mui/material";
import Welcome from "images/Welcome.jpg";
import Icon from "components/icon";
import { useNavigate } from "react-router-dom";
// Import 8 icon images
import Icon1 from "images/Icons1.png";
import Icon2 from "images/Icons2.png";
import Icon3 from "images/Icons3.png";
import Icon4 from "images/Icons4.png";
import Icon5 from "images/Icons5.png";
import Icon6 from "images/Icons6.png";
import Icon7 from "images/Icons7.png";
import Icon8 from "images/Icons8.png";
import Icon9 from "images/Icons0.png";
import Icon10 from "images/Icons9.png";

const WelcomeScreen = ({ }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); 
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); 
  const [currentPage, setCurrentPage] = useState(0);
  const [showIcons, setShowIcons] = useState(false);
  const [visibleIcons, setVisibleIcons] = useState(Array(10).fill(false));
  const [focusedCard, setFocusedCard] = useState(0);

    const getTrialEndDate = () => {
    const today = new Date();
    const trialEndDate = new Date(today);
    trialEndDate.setDate(today.getDate() + 13);
    return trialEndDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const trialEndDate = getTrialEndDate();

  useEffect(() => {
    if (currentPage === 0) {
      const timeouts = [];

      for (let i = 0; i < 10; i++) {
        const timeout = setTimeout(() => {
          setVisibleIcons((prev) => {
            const updated = [...prev];
            updated[i] = true;
            return updated;
          });
        }, i * 170);
        timeouts.push(timeout);
      }

      return () => timeouts.forEach(clearTimeout);
    } else {
      setVisibleIcons(Array(10).fill(false));
    }
  }, [currentPage]);

  // Define pages after calculating trialEndDate
  const pages = [
    // First Welcome Page
    {
      title: "Welcome to Cleri!",
      content: "Your **14 Day Free Trial** starts today! You officially have access to all of Cleri's powerful features.",
      subtitle: "Now, let's get you set up",
      buttonText: "Get Started",
      showCard: false,
      backgroundImage: Welcome,
      icons: [
        { image: Icon1, label: "Payments" },
        { image: Icon2, label: "Users" },
        { image: Icon3, label: "Time Tracking" },
        { image: Icon4, label: "Communication" },
        { image: Icon5, label: "Scheduling" },
        { image: Icon6, label: "Analytics" },
        { image: Icon7, label: "Reports" },
        { image: Icon8, label: "Settings" },
        { image: Icon9, label: "Support" },
        { image: Icon10, label: "Notifications" },
      ],
    },

    // Second Page
    {
      content: `When your Trial expires on **${trialEndDate}**, you'll be prompted to enter payment and promo information`,
      buttonText: "Continue",
      showCard: true,
      singleCard: true,
      backgroundImage: Welcome,
      icon: "iconoir:hand-cash",
      iconColor: "#23A26D",
    },

    // Third Page (two cards)
    {
      buttonText: "Continue",
      showCard: true,
      singleCard: false,
      backgroundImage: Welcome,
      cards: [
        {
          content: `When your Trial expires on **${trialEndDate}**, you'll be prompted to enter payment and promo information`,
          icon: "iconoir:hand-cash",
          iconColor: "#23A26D",
        },
        {
          content: "You will be charged for **Additional Users**, but not Employees. You can enter all of your Employees into Cleri for management purposes",
          icon: "solar:user-broken",
          iconColor: "#23A26D",
        },
      ],
    },

    // Fourth Page (three cards - all in same line)
    {
      buttonText: "Continue",
      showCard: true,
      specialLayout: true,
      backgroundImage: Welcome,
      cardCount: 3,
      cards: [
        {
          content: `When your Trial expires on **${trialEndDate}**, you'll be prompted to enter payment and promo information`,
          icon: "iconoir:hand-cash",
          iconColor: "#23A26D",
        },
        {
          content: "You will be charged for **Additional Users**, but not Employees. You can enter all of your Employees into Cleri for management purposes",
          icon: "solar:user-broken",
          iconColor: "#23A26D",
        },
        {
          content: "Admin and Crew Leaders can **Punch In/Out** their Employee's time cards!",
          icon: "famicons:timer-outline",
          iconColor: "#23A26D",
        },
      ],
    },
    {
      buttonText: "Finish Setup",
      showCard: true,
      gridLayout: true,
      cardCount: 4,
      backgroundImage: Welcome,
      cards: [
        {
          content: `When your Trial expires on **${trialEndDate}**, you'll be prompted to enter payment and promo information`,
          icon: "iconoir:hand-cash",
          iconColor: "#23A26D",
        },
        {
          content: "You will be charged for **Additional Users**, but not Employees. You can enter all of your Employees into Cleri for management purposes",
          icon: "solar:user-broken",
          iconColor: "#23A26D",
        },
        {
          content: "Admin and Crew Leaders can **Punch In/Out** their Employee's time cards!",
          icon: "famicons:timer-outline",
          iconColor: "#23A26D",
        },
        {
          content: "Users are granted access and can utilize your Cleri account. You can start as the **Sole User**, and **invite other Employees** to become a User as you see fit.",
          icon: "tabler:message-user",
          iconColor: "#23A26D",
        },
      ],
    },
  ];

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    } else {
      navigate("/onboarding");
    }
  };

  const renderBoldText = (text) => {
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, i) =>
      i % 2 === 1 ? (
        <span key={i} style={{ fontWeight: 600, color: "#23A26D" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const current = pages[currentPage];
  const isLastIndex = (index) => index === (current?.cards?.length ?? 0) - 1;

  return (
<Box
  sx={{
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${current.backgroundImage})`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: (current.gridLayout && current.cardCount === 4 && isMobile && !isTablet) ? "flex-start" : "center",
    p: isMobile ? 2 : 3,
    textAlign: "center",
    backgroundSize: "cover",
    backgroundPosition: "center",
    overflow: "auto",
    pt: (current.gridLayout && current.cardCount === 4 && isMobile && !isTablet) ? 2 : 0,
  }}
>
      {current.showCard ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: isMobile ? 2 : 4,
            mb: 1,
            position: "relative",
            zIndex: 2,
            width: "100%",
            px: isMobile ? 1 : 0,
          }}
        >
          {current.gridLayout ? (
            <Box
              sx={{ 
                display: "flex", 
                flexDirection: isMobile ? "column" : "row",
                flexWrap: isTablet ? "wrap" : "nowrap",
                justifyContent: "center",
                gap: isMobile ? 2 : isTablet ? 3 : 5,
                width: "100%",
                maxWidth: isMobile ? "100%" : "1200px",
                px: isMobile ? 1 : 0,
              }}
            >
            {current.cards.map((card, index) => {
  const isLast = isLastIndex(index);

  return (
    <Card
      key={index}
      sx={{
        p: isMobile ? 1.5 : 3,
        width: isMobile ? "100%" : "330px",
        minHeight: isMobile ? "170px" : "320px",
        borderRadius: "12px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        border: "2px solid #E0F2E9",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",

        // ✅ highlight only last card
        opacity: isLast ? 1 : 0.6,
        filter: isLast ? "none" : "blur(0.2px)",
        transform: isLast ? "scale(1)" : "scale(0.97)",
        transition: "all 0.28s ease",
      }}
    >
      <Icon
        icon={card.icon}
        width={isMobile ? 24 : 32}
        height={isMobile ? 24 : 32}
        color={card.iconColor}
        style={{ marginBottom: isMobile ? "4px" : "8px" }}
      />
      <Typography
        variant="body2"
        sx={{
          textAlign: "center",
          color: "#555",
          fontSize: isMobile ? "0.8rem" : "0.85rem",
          lineHeight: 1.4,
          px: isMobile ? 1 : 0,
        }}
      >
        {renderBoldText(card.content)}
      </Typography>
    </Card>
  );
})}

            </Box>
          ) : current.specialLayout ? (
            // MODIFIED: All three cards in the same row
            <Box sx={{ 
              display: "flex", 
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 4,
              width: "100%",
              justifyContent: "center",
              flexWrap: isTablet ? "wrap" : "nowrap",
            }}>
              {current.cards.map((card, index) => {
                const isLast = isLastIndex(index);

                return (
                  <Card
                    key={index}
                    sx={{
                      p: isMobile ? 1.5 : 2,
                      width: isMobile ? "100%" : "300px",
                      minHeight: isMobile ? "180px" : "320px",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(95, 154, 98, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #E0F2E9",
                      mx: isMobile ? "auto" : 0,
                      opacity: isLast ? 1 : 0.6,
                      filter: isLast ? "none" : "blur(0.2px)",
                      transform: isLast ? "scale(1)" : "scale(0.97)",
                      transition: "all 0.28s ease",
                    }}
                  >
                    <Icon
                      icon={card.icon}
                      width={isMobile ? 24 : 32}
                      height={isMobile ? 24 : 32}
                      color={card.iconColor}
                      style={{ marginBottom: isMobile ? "4px" : "8px" }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.4,
                        color: "#555",
                        textAlign: "center",
                        fontSize: isMobile ? "0.8rem" : "0.85rem",
                        px: isMobile ? 1 : 0,
                      }}
                    >
                      {renderBoldText(card.content)}
                    </Typography>
                  </Card>
                );
              })}
            </Box>
          ) : current.singleCard ? (
            <Card
              sx={{
                p: isMobile ? 2 : 3,
                width: isMobile ? "100%" : "320px",
                height: isMobile ? "auto" : "320px",
                minHeight: isMobile ? "220px" : "auto",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #E0F2E9",
                mx: isMobile ? "auto" : 0,
              }}
            >
              <Icon
                icon={current.icon}
                width={isMobile ? 32 : 48}
                height={isMobile ? 32 : 48}
                color={current.iconColor}
                style={{ marginBottom: isMobile ? "8px" : "16px" }}
              />
              <Typography
                variant={isMobile ? "body2" : "body1"}
                sx={{
                  lineHeight: 1.5,
                  color: "#555",
                  textAlign: "center",
                  px: isMobile ? 1 : 0,
                }}
              >
                {renderBoldText(current.content)}
              </Typography>
            </Card>
          ) : (
            // MODIFIED: For two cards, blur the first one and highlight the second
            <Box sx={{ 
              display: "flex", 
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 9,
              width: "100%",
              justifyContent: "center",
            }}>
              {current.cards.map((card, index) => {
                const isLast = isLastIndex(index);
                const isTwoCards = current.cards.length === 2;
                
                return (
                  <Card
                    key={index}
                    sx={{
                      p: isMobile ? 2 : 3,
                      width: isMobile ? "100%" : "320px",
                      height: isMobile ? "auto" : "320px",
                      minHeight: isMobile ? "220px" : "auto",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid #E0F2E9",
                      mx: isMobile ? "auto" : 0,
                      // For two cards, blur the first one and highlight the second
                      opacity: isTwoCards ? (isLast ? 1 : 0.6) : 1,
                      filter: isTwoCards ? (isLast ? "none" : "blur(0.2px)") : "none",
                      transform: isTwoCards ? (isLast ? "scale(1)" : "scale(0.97)") : "scale(1)",
                      transition: "all 0.28s ease",
                    }}
                  >
                    <Icon
                      icon={card.icon}
                      width={isMobile ? 32 : 48}
                      height={isMobile ? 32 : 48}
                      color={card.iconColor}
                      style={{ marginBottom: isMobile ? "8px" : "16px" }}
                    />
                    <Typography
                      variant={isMobile ? "body2" : "body1"}
                      sx={{
                        lineHeight: 1.5,
                        color: "#555",
                        textAlign: "center",
                        px: isMobile ? 1 : 0,
                      }}
                    >
                      {renderBoldText(card.content)}
                    </Typography>
                  </Card>
                );
              })}
            </Box>
          )}

          {currentPage < pages.length && (
            <Box
              onClick={handleNext}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "white",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                cursor: "pointer",
                transition: "transform 0.2s",
                ":hover": {
                  transform: "scale(1.1)",
                },
                marginTop:
                  current.specialLayout || current.gridLayout ? (isMobile ? "8px" : "16px") : "0",
                mt: isMobile ? 3 : 0,
              }}
            >
              <Icon
                icon="material-symbols:arrow-forward"
                width={24}
                height={24}
                color="#23A26D"
              />
            </Box>
          )}
        </Box>
      ) : (
        <>
          <Typography
            variant={isMobile ? "h4" : "h2"}
            sx={{
              fontWeight: 500,
              mb: isMobile ? 1 : 2,
              background: "linear-gradient(135deg, #003A70 0%, #23A26D 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: isMobile ? "1.8rem" : "2.6rem",
              position: "relative",
              zIndex: 2,
              px: isMobile ? 2 : 0,
            }}
          >
            {current.title}
          </Typography>

          <Typography
            variant={isMobile ? "body1" : "h5"}
            sx={{
              mb: isMobile ? 4 : 6,
              lineHeight: 1.6,
              maxWidth: isMobile ? "100%" : "600px",
              fontSize: isMobile ? "1rem" : "1.23rem",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: '#0D5235',
              display: "inline",
              fontWeight: 400,
              position: "relative",
              zIndex: 2,
              px: isMobile ? 2 : 0,
            }}
          >
            Your{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              14 Day Free Trial
            </Box>{" "}
            starts today! You officially have access to all of Cleri's powerful
            features.
          </Typography>

          {currentPage === 0 && visibleIcons && (
            <>
              {current.icons.map((iconData, index) => {
               const positions = (isMobile || isTablet) 
  ? [
      { top: "10%", left: "5%" },
      { top: "30%", right: "5%" },
      { bottom: "35%", left: "5%" },
      { bottom: "15%", right: "10%" },
      { top: "15%", right: "15%" },
      { bottom: "10%", right: "5%" },
      { top: "70%", left: "5%" },
      { bottom: "70%", right: "5%" },
      { top: "15%", left: "15%" },
      { bottom: "15%", left: "15%" },
    ]
  : [
      { top: "17%", left: "10%" },
      { top: "40%", right: "18%" },
      { bottom: "47%", left: "18%" },
      { bottom: "20%", right: "25%" },
      { top: "20%", right: "35%" },
      { bottom: "15%", right: "10%" },
      { top: "75%", left: "10%" },
      { bottom: "75%", right: "15%" },
      { top: "20%", left: "30%" },
      { bottom: "20%", left: "35%" },
    ];


                return (
                  <Box
                    key={index}
                    sx={{
                      position: "absolute",
                      ...positions[index],
                      opacity: visibleIcons[index] ? 1 : 0,
                      transform: visibleIcons[index]
                        ? "translateY(0)"
                        : "translateY(20px)",
                      transition: `opacity 0.6s ease ${
                        index * 0.1
                      }s, transform 0.6s ease ${index * 0.1}s`,
                      zIndex: 1,
                      "&:hover": {
                        transform: "scale(1.1)",
                        zIndex: 3,
                      },
                    }}
                  >
                    <img
                      src={iconData.image}
                      alt={iconData.label}
                      style={{
                        width: isMobile ? "60px" : "85%",
                        height: isMobile ? "60px" : "85%",
                        objectFit: "contain",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: isMobile ? -25 : -30,
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: isMobile ? "0.6rem" : "0.7rem",
                        fontWeight: 500,
                        color: "#555",
                        whiteSpace: "nowrap",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                    >
                      {iconData.label}
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        top: 0,
                        left: 0,
                        "&:hover + div": {
                          opacity: 1,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </>
          )}

          <Typography
            variant={isMobile ? "body1" : "body2"}
            sx={{
              color: '#0D5235',
              mb: isMobile ? 2 : 3,
              fontWeight: 400,
              fontSize: isMobile ? "1rem" : "1.2rem",
              position: "relative",
              zIndex: 2,
              px: isMobile ? 2 : 0,
            }}
          >
            {current.subtitle}
          </Typography>

          <Button
            variant="contained"
            size="small"
            onClick={handleNext}
            sx={{
              width: isMobile ? "120px" : "130px",
              height: isMobile ? "36px" : "38px",
              fontSize: isMobile ? "0.9rem" : "1rem",
              fontWeight: 500,
              mt: isMobile ? 2 : 3,
              background: "linear-gradient(135deg, #003A70 0%, #23A26D 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #003A70 0%, #23A26D 100%)",
                opacity: 0.9,
                transform: "scale(1.05)",
              },
              color: "white",
              textTransform: "none",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(35, 162, 109, 0.3)",
              transition: "all 0.3s ease",
              position: "relative",
              zIndex: 2,
            }}
          >
            {current.buttonText}
          </Button>
        </>
      )}
    </Box>
  );
};

export default WelcomeScreen;